import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Autosuggest from 'react-autosuggest';
import { debounce } from '../../utils/utils';
import * as api from '../../utils/api.jsx';
import {
  setAddress,
  setPosition,
  setPositionInput,
  setPlaceId,
} from '../../actions/course.jsx';
import Box from '../shared/atoms/Box';
import Text from '../shared/atoms/Text';
import Button from '../shared/atoms/Button';
import Pin24 from '../icons/Pin24';
import Geoloc24 from '../icons/Geoloc24';
import useNavigatorPosition from '../../hooks/useNavigatorPosition';

const Container = styled(Box)`
  max-width: 100%;
  padding: 0;
`;

const Description = styled(Text)`
  color: ${(p) => p.theme.colors.gray.darkest};
  margin-bottom: 1rem;
  line-height: 1.4;
  font-size: 0.875em;
  ${(p) => p.theme.mediaQueries.tablet} {
    font-size: 0.9375em;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 0.75rem;
`;

const FloatingLabel = styled.label`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.875em;
  color: ${(p) => p.theme.colors.gray.main};
  font-weight: 400;
  pointer-events: none;
  transition: all 0.2s ease;
  background-color: transparent;
  padding: 0 4px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 0.875em;
  font-weight: 400;
  border: 2px solid ${(p) => p.theme.colors.gray.light};
  border-radius: ${(p) => p.theme.radii.xxs};
  outline: none;
  transition: all 0.2s;
  font-family: inherit;
  background-color: white;

  &:focus {
    border-color: #595fe7;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(89, 95, 231, 0.1);
  }

  &:focus ~ ${FloatingLabel}, &:not(:placeholder-shown) ~ ${FloatingLabel} {
    top: 0;
    transform: translateY(-50%);
    font-size: 0.75em;
    color: #595fe7;
    background-color: white;
  }

  &::placeholder {
    color: transparent;
  }
`;

const IconWrapper = styled.div`
  display: none;
`;

const SuggestionsWrapper = styled.div`
  background: white;
  border-radius: ${(p) => p.theme.radii.xxs};
  overflow: hidden;
  margin-bottom: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const SuggestionItem = styled.div`
  padding: 5px 10px !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  border-bottom: none !important;
  background-color: white !important;
  margin: 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;

  &:last-child {
    border-bottom: none !important;
  }

  &:hover {
    background-color: #ddd !important;
  }

  ${(p) =>
    p.highlighted &&
    `
    background-color: #ddd !important;
  `}
`;

const SuggestionIcon = styled.div`
  display: none;
`;

const SuggestionText = styled.div`
  flex: 1;
  font-weight: 400;
  font-size: 0.875em;
  line-height: 1.2;
  color: ${(p) => p.theme.colors.black};
`;

const GeolocSuggestion = styled(SuggestionItem)`
  display: none;
`;

const GeolocText = styled.div`
  flex: 1;
`;

const GeolocTitle = styled(Text)`
  font-weight: 600;
  color: ${(p) => p.theme.colors.black};
  margin-bottom: 2px;
`;

const GeolocSubtitle = styled(Text)`
  font-size: 0.875em;
  color: ${(p) => p.theme.colors.gray.darker};
`;

const CTAButton = styled(Button)`
  width: 100%;
  padding: 14px 20px;
  font-size: 0.9375em;
  margin-top: 0.75rem;
  font-weight: 600;
`;

export default function SimplifiedAddressSearch({ isInZone }) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    isError,
    setNavigatorPosition,
    position: positionNavigator,
  } = useNavigatorPosition();

  const { positionInput, position, positionLoading } = useSelector(
    (s) => s.course,
  );

  const onChange = (event, { newValue }) => {
    event.preventDefault();
    dispatch(setPosition(''));
    dispatch(setPositionInput(newValue));
    setShowSuggestions(true);
  };

  const onSuggestionSelected = async (event, { suggestion }) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (suggestion && suggestion.place_id) {
      setShowSuggestions(false);
      setSuggestions([]);
      dispatch(setPlaceId(suggestion.place_id));
      await dispatch(setAddress(suggestion.description));
    }
  };

  const handleGeolocClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!positionNavigator) {
      setNavigatorPosition();
    } else {
      dispatch(setPosition(positionNavigator));
      dispatch(setAddress(positionNavigator));
      setShowSuggestions(false);
    }
  };

  const handleCTAClick = () => {
    if (position) {
      if (isInZone) {
        // If in zone, redirect to autodiag
        const encodedPosition = encodeURIComponent(position);
        history.push(
          `/reparation/autodiag?autodiag=v2&position=${encodedPosition}`,
        );
      }
      // If not in zone, stay on page and shops will be displayed by InfosComponents
    }
  };

  const fetch = async (input) => {
    const response = await api.get('/zones/addresses', { input });
    if (response.predictions) {
      setSuggestions(response.predictions);
      setShowSuggestions(true);
    }
  };

  const debounceFetch = React.useCallback(
    debounce((query) => fetch(query), 500),
    [],
  );

  const onSuggestionsFetchRequested = ({ value }) => {
    if (value && value.length >= 3) {
      debounceFetch(value);
    } else {
      setSuggestions([]);
    }
  };

  const getSuggestionValue = (item) => item.description || '';

  const onSuggestionsClearRequested = () => {
    // Don't clear suggestions - keep them visible
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    // Show suggestions when input is focused or has content
    const shouldShow = showSuggestions || positionInput;
    if (!shouldShow) return null;

    return (
      <SuggestionsWrapper {...containerProps}>{children}</SuggestionsWrapper>
    );
  };

  const renderSuggestion = (item, { isHighlighted }) => {
    if (!item.description) return null;

    return (
      <SuggestionItem highlighted={isHighlighted}>
        <SuggestionIcon>
          <Pin24 />
        </SuggestionIcon>
        <SuggestionText>{item.description}</SuggestionText>
      </SuggestionItem>
    );
  };

  const inputProps = {
    placeholder: t('repairdefault.search.placeholder'),
    onChange,
    value: positionInput,
    onFocus: () => setShowSuggestions(true),
  };

  return (
    <Container>
      <Description>{t('repairdefault.search.description')}</Description>

      <InputWrapper>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          renderSuggestionsContainer={renderSuggestionsContainer}
          getSuggestionValue={getSuggestionValue}
          alwaysRenderSuggestions={false}
          renderSuggestion={renderSuggestion}
          onSuggestionSelected={onSuggestionSelected}
          inputProps={inputProps}
          renderInputComponent={(inputProps) => (
            <>
              <StyledInput {...inputProps} placeholder=" " />
              <FloatingLabel>
                {t('repairdefault.search.placeholder')}
              </FloatingLabel>
            </>
          )}
        />
      </InputWrapper>

      <CTAButton
        variant="filled"
        disabled={!position || !isInZone}
        onClick={handleCTAClick}>
        {t('repairdefault.search.cta')}
      </CTAButton>
    </Container>
  );
}
